import type { TeamItem } from './types'

export type TeamVipTier = 'none' | 'v1' | 'v2' | 'v3' | 'v4' | 'v5'

export type PointInsufficientScenario = 'buy_or_upgrade' | 'buy_only' | 'contact_admin'

type TeamCommercialAccessTarget = Partial<
  Pick<TeamItem, 'isPersonalTeam' | 'teamKind' | 'memberRole' | 'planLevel' | 'planName'>
> & {
  current_plan_level?: number | string | null
  current_plan_name?: string
  is_personal_team?: boolean | number | string
  member_role?: string
  plan_level?: number | string | null
  plan_name?: string
  team_kind?: string
}

function readText(value: unknown) {
  if (typeof value === 'string') {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

function readNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value)

    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return null
}

function readFlag(value: unknown) {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value === 1
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()

    return normalized === '1' || normalized === 'true'
  }

  return false
}

function readTeamKind(team: TeamCommercialAccessTarget | null | undefined) {
  return readText(team?.teamKind) || readText(team?.team_kind)
}

function readTeamMemberRole(team: TeamCommercialAccessTarget | null | undefined) {
  return readText(team?.memberRole) || readText(team?.member_role)
}

function readTeamPlanName(team: TeamCommercialAccessTarget | null | undefined) {
  return (
    readText(team?.planName) ||
    readText(team?.plan_name) ||
    readText(team?.current_plan_name)
  )
}

function readTeamPlanLevel(team: TeamCommercialAccessTarget | null | undefined) {
  return (
    readNumber(team?.planLevel) ??
    readNumber(team?.plan_level) ??
    readNumber(team?.current_plan_level)
  )
}

export function isPersonalTeamAccount(team: TeamCommercialAccessTarget | null | undefined) {
  if (!team) {
    return false
  }

  return (
    readFlag(team.isPersonalTeam) ||
    readFlag(team.is_personal_team) ||
    readTeamKind(team).toLowerCase() === 'personal'
  )
}

export function isTeamManagerRole(memberRole: string) {
  const normalized = memberRole.trim().toLowerCase()

  if (!normalized) {
    return false
  }

  return (
    normalized.includes('owner') ||
    normalized.includes('admin') ||
    normalized.includes('creator') ||
    normalized.includes('manager') ||
    normalized.includes('\u7ba1\u7406\u5458') ||
    normalized.includes('\u62e5\u6709\u8005')
  )
}

export function formatTeamMemberRole(value: string) {
  const normalized = value.trim().toLowerCase()

  if (!normalized) {
    return '\u6210\u5458'
  }

  if (isTeamManagerRole(value)) {
    return '\u7ba1\u7406\u5458'
  }

  if (normalized.includes('viewer') || normalized.includes('readonly') || normalized.includes('\u53ea\u8bfb')) {
    return '\u53ea\u8bfb'
  }

  if (normalized.includes('editor') || normalized.includes('member') || normalized.includes('\u6210\u5458')) {
    return '\u6210\u5458'
  }

  return value.trim()
}

export function isVip(team?: TeamItem) {
  return team && team.planStatus === 'active'
}

export function resolveTeamVipTier(
  team: Pick<TeamItem, 'planLevel' | 'planName'> | TeamCommercialAccessTarget | null | undefined,
): TeamVipTier {
  if (!team) {
    return 'none'
  }

  const planLevel = readTeamPlanLevel(team)

  if (planLevel !== null) {
    if (planLevel >= 5) {
      return 'v5'
    }
    if (planLevel >= 4) {
      return 'v4'
    }
    if (planLevel >= 3) {
      return 'v3'
    }
    if (planLevel >= 2) {
      return 'v2'
    }
    if (planLevel >= 1) {
      return 'v1'
    }
  }

  const planName = readTeamPlanName(team).toLowerCase()

  if (planName.includes('v5')) {
    return 'v5'
  }
  if (planName.includes('v4')) {
    return 'v4'
  }
  if (planName.includes('v3')) {
    return 'v3'
  }
  if (planName.includes('v2')) {
    return 'v2'
  }
  if (planName.includes('v1')) {
    return 'v1'
  }

  return 'none'
}

export function isHighestVipTier(
  team: Pick<TeamItem, 'planLevel' | 'planName'> | TeamCommercialAccessTarget | null | undefined,
) {
  return resolveTeamVipTier(team) === 'v5'
}

export function resolvePointInsufficientScenario(
  team:
    | Pick<TeamItem, 'isPersonalTeam' | 'teamKind' | 'memberRole' | 'planLevel' | 'planName'>
    | TeamCommercialAccessTarget
    | null
    | undefined,
): PointInsufficientScenario {
  if (!team) {
    return 'buy_or_upgrade'
  }

  if (!isPersonalTeamAccount(team) && !isTeamManagerRole(readTeamMemberRole(team))) {
    return 'contact_admin'
  }

  if (isHighestVipTier(team)) {
    return 'buy_only'
  }

  return 'buy_or_upgrade'
}
